import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FIRM } from "@/lib/site";

const POINTS = [
  ["User-initiated access.", "The user is accessing this website on his/her own initiative, and there has been no invitation, solicitation, or inducement of any kind by Arjun Singh, Advocate, or Law Office of Arjun Singh to create an advocate-client relationship through this website."],
  ["Purpose of information.", "The information on this website is made available only at the user's own request, strictly for informational purposes, and must not be construed as advertising or solicitation of work."],
  ["No advocate-client relationship.", "Browsing, transmitting, or receiving content from this website does not create an advocate-client relationship. No visitor should act, or refrain from acting, on the basis of any content here without first seeking independent legal advice from a qualified advocate."],
  ["No warranty.", "All content is provided \u201Cas is,\u201D without warranty of any kind, express or implied, as to its accuracy, completeness, currency, or reliability. Laws change; a page on this site may not reflect the latest position."],
  ["Not a substitute for legal advice.", "Nothing on this website is, or is intended to be, legal advice. Every matter turns on its own facts \u2014 a wise litigant checks with counsel before acting, not after."],
  ["Limitation of liability.", "Arjun Singh, Advocate, and Law Office of Arjun Singh accept no liability for any loss or damage arising from reliance on material found on this website."],
  ["Third-party links.", "This website may link to third-party sites. Arjun Singh, Advocate, does not endorse and is not responsible for the content of any linked external website."],
  ["Jurisdiction.", "Any dispute arising out of the use of this website shall be subject to the exclusive jurisdiction of the courts at Jabalpur, Madhya Pradesh."],
];

export const Disclaimer = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Always show the disclaimer on every visit (legal requirement).
    setOpen(true);
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const agree = () => {
    document.body.style.overflow = "";
    if (window.__lenis) window.__lenis.start();
    setOpen(false);
  };

  const disagree = () => {
    window.location.href = "https://www.google.com";
  };

  useEffect(() => {
    if (open && window.__lenis) window.__lenis.stop();
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="disclaimer-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-6 bg-[#0A1128]/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
            className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white border border-[#D4AF37]/40 shadow-2xl">
            <div className="shrink-0 border-b border-[#E5E7EB] px-7 md:px-10 py-6">
              <div className="overline text-[#B8962E] mb-2">Please Read Before Proceeding</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#0A1128]">Disclaimer</h2>
            </div>

            <div data-lenis-prevent data-testid="disclaimer-scroll"
              className="flex-1 overflow-y-auto overscroll-contain px-7 md:px-10 py-6"
              style={{ WebkitOverflowScrolling: "touch" }}>
              <p className="font-body text-sm text-[#4b5563] leading-relaxed">
                This website, <span className="text-[#0A1128] font-medium">advocatearjunsingh.com</span>, is owned by the {FIRM.name}.
              </p>
              <p className="font-body text-sm text-[#4b5563] leading-relaxed mt-4">
                Rule 36, Bar Council of India Rules, 1975 (Chapter II, Part VI), bars an advocate from
                soliciting work or advertising, directly or indirectly. This website is not an
                advertisement or solicitation. By clicking &ldquo;I Agree,&rdquo; the user confirms and
                acknowledges the following:
              </p>
              <ol className="mt-6 space-y-4">
                {POINTS.map(([head, body], i) => (
                  <li key={i} className="flex gap-4">
                    <span className="font-mono-meta text-[#D4AF37] text-xs pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-body text-sm text-[#4b5563] leading-relaxed">
                      <span className="text-[#0A1128] font-medium">{head}</span> {body}
                    </p>
                  </li>
                ))}
              </ol>
              <p className="font-body text-sm text-[#4b5563] leading-relaxed mt-6">
                By clicking &ldquo;I Agree&rdquo; below, you confirm you have read and understood this
                disclaimer and wish to proceed at your own volition.
              </p>
            </div>

            <div className="shrink-0 border-t border-[#E5E7EB] px-7 md:px-10 py-5 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button data-testid="disclaimer-disagree-btn" onClick={disagree}
                className="order-2 sm:order-1 border border-[#E5E7EB] text-[#4b5563] px-6 py-3 font-medium hover:border-[#0A1128] hover:text-[#0A1128] transition-colors">
                I Disagree
              </button>
              <button data-testid="disclaimer-agree-btn" onClick={agree}
                className="order-1 sm:order-2 bg-[#0A1128] text-white px-8 py-3 font-medium hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors">
                I Agree
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
